import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import { useContext, useMemo, useState } from "react";
// import { ShareConnectorDialog } from "./shareDialog";
import { useAuth0 } from "@auth0/auth0-react";
import { deleteSharedConnectors } from "api/ComponentsApi";
import { ThemedMailIcon } from "assets/CXFIcons/Static";
import SoftTypography from "components/SoftTypography";
import { PERMISSIONS } from "constants/portal";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import StatusBadge from "cxfcomponents/StatusBadge";
import DataTable from "cxfcomponents/Tables/DataTable";
import { useSharedConnectors } from "hooks/useSharedConnectors";
import DefaultUserAvatar from "layouts/pages/account/components/Header/components/DefaultUserAvatar";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { removeSharedConnectors } from "redux/actions/portal/connectorsActionCreator";
import { RemoveUserDialog } from "../../removeUser";
import { ShareConnectorDialog } from "../../shareConnector";
import { UserRole } from "./user/role";
import { useResourceListener } from "hooks/useResourceListener";

export const Users = ({ connector, isReadOnlyAccess }) => {
   const [searchName, setSearchName] = useState("");
   const [openShareModal, setOpenShareModal] = useState(false);
   const { users } = useSelector((store) => store.portal.users);
   const { sharedConnectors, handleFetchSharedConnectors } = useSharedConnectors();
   const [userToBeRemoved, setUserToBeRemoved] = useState(null);
   const { userPermissions } = useContext(PermissionsContext);
   const dispatch = useDispatch();
   const { user: auth0User } = useAuth0();
   useResourceListener("connectorsSharingUpdate", handleFetchSharedConnectors);

   // Get current user id
   const currentUserId = auth0User.cxfUserId || auth0User.sub;

   // Is current user the owner?
   const isCurrentUserOwner = connector?.created_by_id === currentUserId;

   // Does current user have EDIT_ANY_CONNECTORS?
   const canEditAnyConnectors = userPermissions.some((permission) => permission.name === PERMISSIONS.EDIT_ANY_CONNECTORS && permission.value);

   // Is current user a shared editor?
   const isCurrentUserSharedEditor = sharedConnectors.some((sharedConnector) => sharedConnector.connector_id === connector.id && sharedConnector.shared_user_id === currentUserId && sharedConnector.access_type === "editor");

   // Final permission for current user:
   const canEditConnector = isCurrentUserOwner || canEditAnyConnectors || isCurrentUserSharedEditor;

   const handleAddUsers = () => {
      setOpenShareModal(true);
   };

   const getUsers = () => {
      if (!connector || !users) return;

      const filteredUsers = users.filter((user) => {
         if (!searchName) return true;

         return user.first_name.toLowerCase().includes(searchName) || user.last_name.toLowerCase().includes(searchName) || user.email_address.toLowerCase().includes(searchName);
      });

      const owner = filteredUsers.find((user) => user.auth0_id === connector.created_by_id);

      if (owner) {
         owner.isOwner = true;
      }

      const usersSharedWithConnector = sharedConnectors.filter((sharedConnector) => sharedConnector.connector_id === connector.id);

      const sharedUsers = filteredUsers.filter((user) => usersSharedWithConnector.find((sharedUser) => sharedUser.shared_user_id === user.auth0_id));

      const combinedUsers = [].concat(owner ? owner : [], sharedUsers);

      return combinedUsers;
   };

   const handleSetUserToBeRemoved = (user) => {
      setUserToBeRemoved(user);
   };

   const handleRemoveUser = async () => {
      if (!userToBeRemoved) return;

      const foundSharedConnector = sharedConnectors.find((sharedConnector) => sharedConnector.connector_id === connector.id && sharedConnector.shared_user_id === userToBeRemoved.auth0_id);

      if (!foundSharedConnector) return;

      const result = await deleteSharedConnectors(auth0User?.stripeId, [foundSharedConnector.id]);

      if (result?.isSuccessful) {
         toast.success(`User has been removed successfully!`);
      } else {
         toast.error(`User has been removed unsuccessfully`);
      }

      dispatch(removeSharedConnectors([foundSharedConnector.id]));
      setUserToBeRemoved(null);
   };

   const isOwner = () => users.find((user) => user.auth0_id === auth0User.cxfUserId)?.isOwner;

   const isAllowedAction = (action) => {
      if (isOwner()) return true;

      switch (action) {
         case "share": {
            const sharePermissions = [PERMISSIONS.SHARE_CONNECTORS, PERMISSIONS.SHARE_ANY_CONNECTORS];

            return sharePermissions.some((permission) => userPermissions.some((userPermission) => userPermission.name === permission && userPermission.value));
         }
      }
      return false;
   };

   const tableData = useMemo(() => {
      return {
         columns: [
            {
               Header: "profile",
               accessor: "profile",
               canGroupBy: false,
               disableSortBy: true,
               Cell: ({ value, row }) =>
                  value ? (
                     <SoftBox
                        component="img"
                        src={value}
                        sx={{
                           border: ".5px solid white",
                           borderRadius: "12px",
                           width: "60px",
                           objectFit: "cover",
                           height: "60px",
                           marginRight: "10px"
                        }}
                     />
                  ) : (
                     <DefaultUserAvatar
                        firstName={row?.original?.user?.first_name}
                        lastName={row?.original?.user?.last_name}
                        variant={"rounded"}
                        size="lg"
                        border=".5px solid white !important"
                        disableFocusListener={false}
                        disableTouchListener={false}
                        open={false}
                     />
                  )
            },
            {
               Header: "name",
               accessor: "name",
               width: 200,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox
                     className="white-to-dark"
                     fontSize="14px"
                     fontWeight="bold"
                     display="flex"
                     alignItems="center"
                     sx={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        whiteSpace: "normal",
                        wordBreak: "break-all"
                     }}
                  >
                     {value}
                  </SoftBox>
               )
            },
            {
               Header: "email",
               accessor: "email",
               width: 200,
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox>
                     <SoftTypography color="secondary" sx={{ fontWeight: 400, fontSize: "14px", display: "flex", alignItems: "center" }}>
                        <ThemedMailIcon width="19px" height="19px" sx={{ marginRight: "8px" }} />
                        {value}
                     </SoftTypography>
                  </SoftBox>
               )
            },
            {
               Header: "status",
               accessor: "status",
               canGroupBy: false,
               Cell: ({ value }) => (
                  <SoftBox>
                     <StatusBadge status={value} />
                  </SoftBox>
               )
            },

            {
               Header: "role",
               accessor: "user",
               canGroupBy: false,
               Cell: ({ value }) => (
                  <UserRole
                     user={value}
                     handleSetUserToBeRemoved={handleSetUserToBeRemoved}
                     isAllowedToShare={isAllowedAction("share")}
                     connector={connector}
                     isReadOnlyAccess={isReadOnlyAccess}
                     canEditConnector={canEditConnector}
                  />
               )
            },
            {
               accessor: "id",
               show: false
            }
         ].map((column) => ({ ...column, disableResize: true })),
         rows: getUsers().map((user) => {
            return {
               profile: user.image,
               name: `${user.first_name ?? ""} ${user.last_name ?? ""}`,
               email: user.email_address,
               status: user.status,
               user
            };
         })
      };
   }, [getUsers()]);

   return (
      <>
         <SoftBox>
            <SoftBox
               sx={{
                  alignItems: "flex-start",
                  display: "flex",
                  justifyContent: "flex-start",
                  padding: "12px 0 12px 40px",
                  marginBottom: "1rem"
               }}
            >
               <SoftInput
                  size="medium"
                  placeholder="Search..."
                  value={searchName}
                  onChange={({ currentTarget }) => {
                     setSearchName(currentTarget.value);
                  }}
                  sx={{
                     maxWidth: "380px",
                     marginRight: "34px"
                  }}
               />
               {isAllowedAction("share") && (
                  <SoftButton variant="outlined" color="info" onClick={handleAddUsers}>
                     add
                  </SoftButton>
               )}
            </SoftBox>
            <DataTable table={tableData} entriesPerPage={{ defaultValue: 25 }} hiddenTopButtons={true} noTopPart tableHeight="clamp(350px, calc(100vh - 130px), calc(100vh - 500px))" />
         </SoftBox>
         <ShareConnectorDialog component={connector} open={openShareModal} onClose={() => setOpenShareModal(false)} />
         <RemoveUserDialog isOpen={!!userToBeRemoved} user={userToBeRemoved} onClose={() => setUserToBeRemoved(null)} onAccept={handleRemoveUser} />
      </>
   );
};
